import { X_AXIS_KEY } from "components/Graph/constants.ts";
import { PERCENTAGE_KEYS_LOOKUP, GraphId, MetricValueType, NestedPaths } from "components/Graph/types.ts";
import { GapFiller } from "components/Graph/utils/GapFiller.ts";

export type MetricValueTypeObject =
    | { type: Exclude<MetricValueType, "money"> }
    | { type: Extract<MetricValueType, "money">; currency_id: number };

// TODO: some of the type args are worth revisiting ...
export class GeneralGraphUtils {
    public static filterDataByEntities<T extends Record<string, unknown>>(data: T[], entities: string[]) {
        return data.map((entry) => {
            return Object.fromEntries(
                Object.entries(entry).filter(([key]) => key === X_AXIS_KEY || entities.includes(key))
            );
        });
    }

    public static getValueFromPath<T>(obj: T, path: NestedPaths<T>) {
        if (this.isString(path)) {
            return path.split(".").reduce<unknown>(
                (acc, key) => (acc == null ? undefined : (acc as Record<string, unknown>)[key]),
                obj
            );
        }
    }

    public static getMetricSum<T extends object>(data: T[], key: keyof T) {
        return data?.reduce((acc, item) => acc + this.resolveMetricValueToNumber(item[key]), 0) ?? 0;
    }

    public static getMetricType<T, K extends keyof T>(
        data: T[],
        key: K | undefined,
        graphId: GraphId
    ): MetricValueTypeObject {
        if (key === undefined) {
            return { type: "unknown" };
        }

        const sample = data.find((item) => item[key] !== null && item[key] !== undefined)?.[key];
        if (this.isMoneyData(sample)) {
            return { type: "money", currency_id: sample.currency_id };
        }

        if (PERCENTAGE_KEYS_LOOKUP[graphId]?.includes(key as string)) {
            return { type: "percent" };
        }

        if (this.isNumber(sample)) {
            return { type: "number" };
        }

        if (this.isString(sample)) {
            return { type: "string" };
        }

        return { type: "unknown" };
    }

    public static filterFalsyNullUndefined<T extends unknown>(item: T): item is Exclude<T, null | undefined> {
        return typeof item === "number" || Boolean(item);
    }

    public static resolveMetricValueToNumber(value: unknown) {
        if (typeof value === "number") {
            return value;
        }

        if (this.isMoneyData(value)) {
            return value.amount;
        }

        return 0;
    }

    public static resolveMetricValueToOrigin(value: unknown) {
        if (typeof value === "number" || typeof value === "string") {
            return value;
        }

        if (this.isMoneyData(value)) {
            return value;
        }

        return null;
    }

    public static resolveEntityId(value: unknown) {
        if (typeof value === "string" || typeof value === "number") {
            return String(value);
        }

        if (this.isOfferData(value)) {
            return value.id;
        }

        if (this.isAffiliateData(value)) {
            return value.id;
        }

        return "";
    }

    public static resolveEntityValue(value: unknown) {
        if (typeof value === "string" || typeof value === "number") {
            return value;
        }

        if (this.isOfferData(value)) {
            return value.title;
        }

        if (this.isAffiliateData(value)) {
            return value.nickname || value.email || value.id;
        }

        return null;
    }

    public static hasDateAt(obj: unknown): obj is { date_at: string } {
        return typeof obj === "object" && obj !== null && "date_at" in obj && typeof (obj as any).date_at === "string";
    }

    public static hasDatetimeAt(obj: unknown): obj is { datetime_at: string } {
        return (
            typeof obj === "object" &&
            obj !== null &&
            "datetime_at" in obj &&
            typeof (obj as any).datetime_at === "string"
        );
    }

    public static isOfferData(obj: unknown): obj is App.Data.OfferData {
        return (
            typeof obj === "object" &&
            obj !== null &&
            "id" in obj &&
            "visible_title" in obj &&
            typeof obj.id === "string" &&
            typeof obj.visible_title === "string"
        );
    }

    public static isAffiliateData(obj: unknown): obj is App.Data.UserData {
        return (
            typeof obj === "object" &&
            obj !== null &&
            "id" in obj &&
            "nickname" in obj &&
            typeof obj.id === "string" &&
            (typeof obj.nickname === "string" || obj.nickname === null)
        );
    }

    public static isMoneyData(obj: unknown): obj is App.Data.MoneyData {
        return (
            typeof obj === "object" &&
            obj !== null &&
            "amount" in obj &&
            "currency_id" in obj &&
            typeof obj.amount === "number" &&
            typeof obj.currency_id === "number"
        );
    }

    public static isNumber(value: unknown): value is number {
        return typeof value === "number";
    }

    public static isString(value: unknown): value is string {
        return typeof value === "string";
    }

    private static deepCopy = (obj: object) => {
        // Note: we need to drop the memory reference to the react-query cached object to avoid mutations
        // structuredClone doesn't support functions, DOM nodes, or circular references
        try {
            return structuredClone(obj);
        } catch {
            // Fallback to JSON for edge cases (loses Date objects, undefined values, functions, etc.)
            console.warn("deepCopy: structuredClone failed, using JSON fallback (may lose Date objects, undefined values)");
            return JSON.parse(JSON.stringify(obj));
        }
    };

    public static prepareEntityData = <T extends object>(obj: Record<string, Record<string, T>> | undefined) => {
        if (obj === undefined) {
            return [];
        }

        const _obj = this.deepCopy(obj) as Record<string, Record<string, T>>;
        const mapped = GapFiller.mapEntitiesToObjects(_obj);

        return Object.values(mapped).flatMap((key) => Object.values(key));
    };

    public static prepareTimeData = <T extends object>(obj: Record<string, T> | undefined) => {
        if (obj === undefined) {
            return [];
        }

        const _obj = this.deepCopy(obj) as Record<string, T>;
        const mapped = GapFiller.moveDateKeysIntoObjects(_obj);

        return Object.values(mapped);
    };

    public static filterEmptyObjects = (obj: object) => Object.keys(obj).length > 0;
}
